import React, { useEffect, useState } from 'react';
import { X, Search } from 'lucide-react';
import { Button, Checkbox, Chip, Slider } from '../ui';
import { useAppStore } from '../../context/store';
import { formFactors, sidePanelTypes } from '../../data/cases';
import { useManufacturers } from '../../hooks/useCases';
import { formatDimension } from '../../utils/conversions';

interface FilterPanelProps {
  resultCount: number;
}

// Slide-over panel with every filter that doesn't fit in the filter bar
export const FilterPanel: React.FC<FilterPanelProps> = ({ resultCount }) => {
  const {
    filters,
    comparison,
    toggleFormFactor,
    toggleManufacturer,
    toggleSidePanelType,
    setMinGpuLength,
    setMinCoolerHeight,
    setMinPsuLength,
    setMinRadiatorSize,
    resetFilters,
    filterPanelOpen,
    setFilterPanelOpen,
  } = useAppStore();
  const { manufacturers } = useManufacturers();
  const [brandQuery, setBrandQuery] = useState('');

  const unit = comparison.unitSystem === 'metric' ? 'mm' : 'in';
  const close = () => setFilterPanelOpen(false);

  useEffect(() => {
    if (!filterPanelOpen) {
      return;
    }
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setFilterPanelOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [filterPanelOpen, setFilterPanelOpen]);

  if (!filterPanelOpen) {
    return null;
  }

  const visibleBrands = manufacturers.filter((m) => m.toLowerCase().includes(brandQuery.trim().toLowerCase()));
  const formatClearance = (v: number) => (v === 0 ? 'Any' : formatDimension(v, unit));

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px] animate-fade-in" onClick={close} />

      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Filters"
        className="absolute inset-y-0 right-0 flex w-[400px] max-w-full flex-col border-l border-line bg-surface text-fg shadow-pop animate-slide-up"
      >
        <div className="flex items-center justify-between border-b border-line px-5 py-4">
          <h2 className="text-lg font-semibold tracking-tight">Filters</h2>
          <Button variant="ghost" size="icon" onClick={close} aria-label="Close filters" className="-mr-2">
            <X size={18} />
          </Button>
        </div>

        <div className="flex-1 space-y-7 overflow-y-auto px-5 py-5">
          <Section title="Brand">
            <label className="relative block">
              <span className="sr-only">Search brands</span>
              <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
              <input
                type="search"
                value={brandQuery}
                onChange={(e) => setBrandQuery(e.target.value)}
                placeholder="Search brands…"
                className="h-9 w-full rounded-lg border border-line-strong bg-surface pl-9 pr-3 text-sm text-fg placeholder:text-muted focus:outline-none focus:border-accent"
              />
            </label>
            <ul className="mt-3 max-h-64 overflow-y-auto scrollbar-thin rounded-lg border border-line divide-y divide-line">
              {visibleBrands.map((brand) => (
                <li key={brand} className="hover:bg-surface-2">
                  <Checkbox
                    checked={filters.manufacturers.includes(brand)}
                    onChange={() => toggleManufacturer(brand)}
                    label={brand}
                    className="w-full px-3 py-2.5"
                  />
                </li>
              ))}
              {visibleBrands.length === 0 && <li className="px-3 py-2.5 text-sm text-muted">No brands found.</li>}
            </ul>
          </Section>

          <Section title="Motherboard">
            <div className="flex flex-wrap gap-2">
              {formFactors.map((ff) => (
                <Chip key={ff} size="sm" active={filters.formFactors.includes(ff)} onClick={() => toggleFormFactor(ff)}>
                  {ff}
                </Chip>
              ))}
            </div>
          </Section>

          <Section title="Minimum clearance">
            <div className="space-y-5">
              <Slider label="GPU length" min={0} max={450} step={10} value={filters.minGpuLength} onChange={setMinGpuLength} formatValue={formatClearance} />
              <Slider label="CPU cooler height" min={0} max={200} step={5} value={filters.minCoolerHeight} onChange={setMinCoolerHeight} formatValue={formatClearance} />
              <Slider label="PSU length" min={0} max={250} step={10} value={filters.minPsuLength} onChange={setMinPsuLength} formatValue={formatClearance} />
              <Slider label="Radiator size" min={0} max={420} step={120} value={filters.minRadiatorSize} onChange={setMinRadiatorSize} formatValue={(v) => (v === 0 ? 'Any' : `${v} mm`)} />
            </div>
          </Section>

          <Section title="Side panel">
            <div className="flex flex-wrap gap-2">
              {sidePanelTypes.map((type) => (
                <Chip key={type} size="sm" active={filters.sidePanelTypes.includes(type)} onClick={() => toggleSidePanelType(type)}>
                  {type}
                </Chip>
              ))}
            </div>
          </Section>
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-line px-5 py-4">
          <Button variant="ghost" onClick={resetFilters}>Clear all</Button>
          <Button variant="primary" size="lg" onClick={close}>
            Show {resultCount} {resultCount === 1 ? 'case' : 'cases'}
          </Button>
        </div>
      </aside>
    </div>
  );
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section>
    <h3 className="mb-3 font-mono text-xs uppercase tracking-label text-muted">{title}</h3>
    {children}
  </section>
);
